import { getAppContext } from '../AppContext';
import { ProblemDetails, ValidationProblemDetails } from '../ApiModel';

/** ProblemDetails enriched with extension fields that WebApi's CustomProblemDetailsFactory
 *  attaches via `problemDetails.Extensions` (not part of the OpenAPI-generated type). */
export interface EnrichedProblemDetails extends ProblemDetails {
    request_id_message?: string;
}

export function isUnknownObject(x: unknown): x is { [key in PropertyKey]: unknown } {
    return x !== null && typeof x === 'object';
}

/** Cancellation signal from `UnmountHelper.cancelIfUnmounted` / `PromiseHelper` or an aborted
 *  `fetch`. Not a real error — `PanelApp` suppresses unhandled `AbortError` rejections globally,
 *  and callers that catch rejections explicitly (e.g. `FormStore.doSubmit`, `AsyncButton`) must
 *  filter it out before surfacing errors to the user.
 *
 *  Matches both the native `AbortError` `DOMException` and the form `ApiFetch` produces: it catches
 *  the `DOMException` and re-wraps it as a `NetworkError` `ProblemDetails` with `instance` set to the
 *  error's name (`networkErrorAsProblemDetails`), so an aborted request reaches API-client callers as
 *  a `ProblemDetails`, not a `DOMException`. */
export function isAbortError(e: unknown): boolean {
    if (e instanceof DOMException && e.name === 'AbortError') return true;
    return isProblemDetails(e) && e.instance === 'AbortError';
}

export function isProblemDetails(e: unknown): e is EnrichedProblemDetails {
    return isUnknownObject(e) && typeof e.type === 'string' && typeof e.title === 'string';
}

/** The spec declares `errors` optional because non-validation 400s share this schema, so the guard
 *  narrows it to present — which is what distinguishes a validation failure. */
export function isValidationProblemDetails(
    e: unknown
): e is ValidationProblemDetails & { errors: NonNullable<ValidationProblemDetails['errors']> } {
    return isProblemDetails(e) && !!(e as { errors?: ValidationErrors }).errors;
}

/**
 * True when `e` is a ValidationProblemDetails carrying only field-level errors
 * (no form-level `errors['']`) — the kind a form renders inline next to its fields.
 * Callers that surface errors through the modal use this to skip the modal for these,
 * leaving the inline field messages to do the work. An empty errors object is not
 * inline-renderable, so it is deliberately not treated as an inline field error.
 */
export function isInlineFieldValidationError(e: unknown): boolean {
    return isValidationProblemDetails(e) && !e.errors[''] && Object.keys(e.errors).length > 0;
}

/**
 * Decides what a form-submission error should display in the global error modal, keeping that
 * form-routing policy out of the generic ErrorModalHost. Returns the body to show, or `null`
 * when nothing should surface (an abort, or a field-level error that already renders inline).
 *
 * For a ValidationProblemDetails: an action form with no inline surface (`fieldErrorsToModal`)
 * shows every message; otherwise the field-level messages render inline next to their fields,
 * so only the form-level (`errors['']`) messages are carried to the modal. The caller's human
 * context is not folded in here — it rides separately as `errorSummary` (see `showFormError`).
 */
export function formErrorModalBody(e: unknown, options?: { fieldErrorsToModal?: boolean }): unknown {
    if (isAbortError(e)) return null;
    if (!options?.fieldErrorsToModal && isInlineFieldValidationError(e)) return null;
    if (isValidationProblemDetails(e) && !options?.fieldErrorsToModal) {
        const hasFieldErrors = Object.keys(e.errors).some((key) => key !== '');
        if (hasFieldErrors) {
            // errors[''] is guaranteed present here (isInlineFieldValidationError returned null otherwise).
            return { ...e, errors: { '': e.errors[''] } };
        }
        return e;
    }
    return e;
}

/**
 * Routes a form-submission error to the global error modal using the same policy as
 * `FormStore.doSubmit`: `formErrorModalBody` decides what (if anything) to surface, and a
 * non-null body is passed to `getAppContext().error`. Use at non-`FormStore` submit sites so
 * they stay in lockstep with `doSubmit`'s inline-vs-modal routing.
 *
 * `errorSummary` is the caller's human context (e.g. "Unable to alter disk");
 * the modal renders it as a lead-in above the structured error detail, so every form modal
 * leads with what the user was doing and then the specifics.
 */
export function showFormError(
    e: unknown,
    options?: { fieldErrorsToModal?: boolean; errorSummary?: string; title?: string }
): void {
    const body = formErrorModalBody(e, { fieldErrorsToModal: options?.fieldErrorsToModal });
    if (body !== null) {
        const modalOptions =
            options?.title || options?.errorSummary
                ? {
                      ...(options.title ? { title: options.title } : {}),
                      ...(options.errorSummary ? { errorSummary: options.errorSummary } : {}),
                  }
                : undefined;
        getAppContext().error(body, modalOptions);
    }
}

/**
 * Return `error` with its ValidationProblemDetails field keys remapped via `mapKey`. Any non-validation
 * error (comms failure, 500, AbortError) is returned unchanged, so it still routes to the global error
 * modal. Use to bridge a server whose validation field names differ from the form's field keys.
 */
export function remapValidationErrorKeys(error: unknown, mapKey: (key: string) => string): unknown {
    if (!isValidationProblemDetails(error)) return error;
    const errors = error.errors as Record<string, string[] | undefined>;
    const remapped: Record<string, string[] | undefined> = {};
    for (const key of Object.keys(errors)) {
        const target = mapKey(key);
        // Concatenate on collision, don't overwrite: several source keys can map to one target (the
        // reverse-nameserver caller routes every unrecognised key to ''), and last-wins would silently
        // drop the earlier messages.
        remapped[target] = [...(remapped[target] ?? []), ...(errors[key] ?? [])];
    }
    return { ...error, errors: remapped };
}

export type ValidationErrors<T = Record<string, string>> = { [K in keyof T | '']?: string[] };

export function getErrorsFor(errors: ValidationErrors | undefined, field: string): string[] | undefined {
    // If the input type is ever widened to allow `null`, optional chaining returns `undefined` for that case (not `null`).
    return errors?.[field];
}

export function hasError(errors: ValidationErrors | undefined, field: string): boolean {
    return !!getErrorsFor(errors, field);
}

export function hasErrors(errors: ValidationErrors | undefined): boolean {
    return Object.keys(errors || {}).length > 0;
}

export function errorsStartingWith(errors: ValidationErrors, prefix: string): ValidationErrors {
    const subset: ValidationErrors = {};
    for (const k of Object.keys(errors)) {
        if (k.startsWith(prefix)) {
            subset[k] = errors[k];
        }
    }
    return subset;
}

export function getChildErrors(errors: ValidationErrors, field: string): ValidationErrors {
    const childErrors: ValidationErrors = {};
    const objectPrefix = `${field}.`;
    const arrayPrefix = `${field}[`;
    for (const k of Object.keys(errors)) {
        if (k.startsWith(objectPrefix)) {
            childErrors[k.substring(objectPrefix.length)] = errors[k];
        }
        if (k.startsWith(arrayPrefix)) {
            childErrors[k.substring(arrayPrefix.length - 1)] = errors[k];
        }
    }
    return childErrors;
}

export function hasChildErrors(errors: ValidationErrors, field: string): boolean {
    return Object.keys(getChildErrors(errors, field)).length > 0;
}

export function removeError(errors: ValidationErrors, field: string): ValidationErrors {
    const newErrors: ValidationErrors = {};
    for (const key of Object.keys(errors)) {
        if (key !== field) {
            newErrors[key] = errors[key];
        }
    }
    return newErrors;
}

export function removeChildErrors(errors: ValidationErrors, field: string): ValidationErrors {
    const newErrors: ValidationErrors = {};
    for (const key of Object.keys(errors)) {
        if (!key.startsWith(field + '.') && !key.startsWith(field + '[')) {
            newErrors[key] = errors[key];
        }
    }
    return newErrors;
}

export function buildValidationErrorDetails(errors: ValidationErrors) {
    return {
        type: 'ValidationError',
        detail: 'There were validation errors',
        status: 400,
        title: 'Validation Error',
        errors: errors,
    } as ValidationProblemDetails;
}
